import React, { useState, useEffect, useRef, useCallback } from "react";
import { api, API } from "@/lib/api";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { Send, Sparkles, Trash2, Plus, MessageSquare, Cpu, GitCompare, Square, DollarSign } from "lucide-react";
import ReactMarkdown from "react-markdown";
import AICompareDialog from "@/components/AICompareDialog";

const SUGGESTIONS = [
  "¿En qué gasté más este mes?",
  "¿Me conviene amortizar la hipoteca?",
  "Analiza mi rentabilidad inmobiliaria",
  "¿Cómo va mi ahorro comparado con el mes pasado?",
];

const DEFAULT_MODEL = "anthropic/claude-sonnet-4-6";
const EMPTY_USAGE = { tokens_in: 0, tokens_out: 0, cost_usd: 0 };

const formatCost = (usd) => {
  const n = Number(usd) || 0;
  if (n === 0) return "$0.00";
  if (n < 0.01) return `<$0.01`;
  return `$${n.toFixed(n < 1 ? 4 : 2)}`;
};

/**
 * Shared AI chat panel. `variant`:
 *   - "page": full-screen inline (Sidebar + main area)
 *   - "widget": compact floating widget (no sidebar, one active session)
 */
export function AIChatPanel({ variant = "page" }) {
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null); // { id, messages, title, model, usage_total }
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [models, setModels] = useState([]);
  const [userDefaultModel, setUserDefaultModel] = useState(DEFAULT_MODEL);
  const [selectedModel, setSelectedModel] = useState(DEFAULT_MODEL);
  const [compareOpen, setCompareOpen] = useState(false);
  const [budgetInfo, setBudgetInfo] = useState(null); // {spent, budget, warn, over, pct}
  const [warnedThisMonth, setWarnedThisMonth] = useState(false);
  const listRef = useRef(null);
  const abortRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try {
      const r = await api.get("/ai/sessions");
      setSessions(r.data || []);
    } catch (e) {
      logger.error("chat sessions", e);
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  const loadModels = useCallback(async () => {
    try {
      const [ms, prefs] = await Promise.all([
        api.get("/ai/models"),
        api.get("/ai/prefs"),
      ]);
      setModels(ms.data?.models || []);
      const pref = prefs.data?.model || ms.data?.default || DEFAULT_MODEL;
      setUserDefaultModel(pref);
      setSelectedModel(pref);
    } catch (e) {
      logger.error("ai models", e);
    }
  }, []);

  const loadBudget = useCallback(async () => {
    try {
      const r = await api.get("/ai/usage/current-month");
      setBudgetInfo(r.data || null);
      if (r.data?.warn) {
        setWarnedThisMonth(true);
      } else {
        setWarnedThisMonth(false);
      }
    } catch (e) {
      logger.error("ai usage", e);
    }
  }, []);

  useEffect(() => { loadSessions(); loadModels(); loadBudget(); }, [loadSessions, loadModels, loadBudget]);

  const openSession = async (id) => {
    try {
      const r = await api.get(`/ai/sessions/${id}`);
      setCurrent({ ...r.data, usage_total: r.data.usage_total || EMPTY_USAGE });
      setSelectedModel(r.data?.model || userDefaultModel);
    } catch (e) {
      toast.error("No se pudo abrir el chat");
    }
  };

  const startNew = () => {
    setCurrent({
      id: null,
      messages: [],
      title: "Nuevo chat",
      model: userDefaultModel,
      usage_total: EMPTY_USAGE,
    });
    setSelectedModel(userDefaultModel);
  };

  const deleteSession = async (id) => {
    try {
      await api.delete(`/ai/sessions/${id}`);
      toast.success("Chat eliminado");
      if (current?.id === id) setCurrent(null);
      loadSessions();
    } catch { toast.error("No se pudo eliminar"); }
  };

  const send = async (rawText) => {
    const text = (rawText ?? input).trim();
    if (!text || sending) return;
    setSending(true);
    setInput("");

    // Optimistic UI: append user message + empty assistant placeholder that fills as tokens arrive
    const initialMsgs = [
      ...(current?.messages || []),
      { role: "user", content: text, at: new Date().toISOString() },
      { role: "assistant", content: "", at: new Date().toISOString(), streaming: true },
    ];
    setCurrent((prev) => ({
      id: prev?.id || null,
      title: prev?.title || text.slice(0, 60),
      model: selectedModel,
      usage_total: prev?.usage_total || EMPTY_USAGE,
      messages: initialMsgs,
    }));

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await fetch(`${API}/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          message: text,
          session_id: current?.id || null,
          model: selectedModel,
        }),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) {
        throw new Error(`stream_http_${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let receivedFinalEvent = false;

      const applyEvent = (evt) => {
        if (evt.error) {
          throw new Error(evt.error);
        }
        if (evt.delta) {
          setCurrent((prev) => {
            if (!prev) return prev;
            const msgs = [...prev.messages];
            const last = msgs[msgs.length - 1];
            if (last && last.role === "assistant") {
              msgs[msgs.length - 1] = { ...last, content: (last.content || "") + evt.delta };
            }
            return { ...prev, messages: msgs };
          });
        }
        if (evt.session_id && !current?.id) {
          setCurrent((prev) => prev ? { ...prev, id: evt.session_id } : prev);
        }
        if (evt.done) {
          receivedFinalEvent = true;
          setCurrent((prev) => {
            if (!prev) return prev;
            const msgs = [...prev.messages];
            const last = msgs[msgs.length - 1];
            if (last && last.role === "assistant") {
              msgs[msgs.length - 1] = {
                ...last,
                streaming: false,
                model: evt.model,
                usage: evt.usage,
              };
            }
            return {
              ...prev,
              id: evt.session_id || prev.id,
              title: evt.title || prev.title,
              model: evt.model || prev.model,
              usage_total: evt.usage_total || prev.usage_total,
              messages: msgs,
            };
          });
        }
      };

      // Read chunks and parse SSE frames (`data: ...\n\n`)
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) >= 0) {
          const raw = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 2);
          if (!raw.startsWith("data:")) continue;
          const jsonStr = raw.slice(5).trim();
          if (!jsonStr) continue;
          try {
            applyEvent(JSON.parse(jsonStr));
          } catch (err) {
            logger.error("SSE parse", err, jsonStr);
          }
        }
      }

      if (!receivedFinalEvent) {
        // Server closed without a done event — treat as failure (unless we aborted)
        if (!controller.signal.aborted) {
          throw new Error("stream_closed_prematurely");
        }
      }

      // Refresh sidebar list + budget so the new session/title/spend appears
      loadSessions();
      loadBudget();
    } catch (e) {
      logger.error("send chat", e);
      if (e.name === "AbortError" || controller.signal.aborted) {
        // Abort keeps whatever tokens streamed so far (server also persists them)
        setCurrent((prev) => {
          if (!prev) return prev;
          const msgs = [...prev.messages];
          const last = msgs[msgs.length - 1];
          if (last && last.role === "assistant") {
            if ((last.content || "").length === 0) {
              // No content arrived — drop the empty pair
              return { ...prev, messages: prev.messages.slice(0, -2) };
            }
            msgs[msgs.length - 1] = { ...last, streaming: false, truncated: true };
          }
          return { ...prev, messages: msgs };
        });
        toast.info("Generación detenida");
        loadSessions();
        loadBudget();
      } else {
        toast.error("No se pudo enviar el mensaje");
        setCurrent((prev) => {
          if (!prev) return prev;
          return { ...prev, messages: prev.messages.slice(0, -2) };
        });
      }
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  const stopStreaming = () => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
  };

  // Extract the trailing message content to a stable value so we can put it
  // in the useEffect dependency array without a complex expression there.
  const trailingContent = current?.messages?.[current?.messages?.length - 1]?.content;

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [current?.messages, trailingContent, sending]);

  // Warn once per month when spend crosses 80% of budget.
  // Stored in sessionStorage (not localStorage) so it never persists across
  // browser sessions and never carries any sensitive data — just a boolean.
  useEffect(() => {
    if (!budgetInfo || !warnedThisMonth) return;
    if (typeof window === "undefined") return;
    const key = `ai.budget.warned.${budgetInfo.month}`;
    try {
      if (window.sessionStorage.getItem(key)) return;
    } catch (e) {
      logger.warn("budget warn read failed", e);
      return;
    }
    const spent = Number(budgetInfo.spent || 0);
    const budget = Number(budgetInfo.budget || 0);
    if (budgetInfo.over) {
      toast.error(`Has superado el presupuesto mensual de IA ($${budget.toFixed(2)}). Llevas $${spent.toFixed(4)}.`);
    } else {
      toast.warning(`Presupuesto mensual IA al ${budgetInfo.pct.toFixed(0)}% ($${spent.toFixed(4)} / $${budget.toFixed(2)}).`);
    }
    try {
      window.sessionStorage.setItem(key, "1");
    } catch (e) {
      logger.warn("budget warn write failed", e);
    }
  }, [budgetInfo, warnedThisMonth]);

  const modelSelector = models.length > 0 && (
    <div className="flex items-center gap-2 text-xs text-[#5F7368]">
      <Cpu className="w-3.5 h-3.5 text-[#1C7C54]" />
      <label htmlFor="ai-model-select" className="hidden sm:inline">Modelo</label>
      <select
        id="ai-model-select"
        data-testid="ai-model-select"
        value={selectedModel}
        onChange={(e) => setSelectedModel(e.target.value)}
        disabled={sending}
        className="h-8 px-2 rounded-lg border border-[#EBEBEA] bg-white text-xs text-[#0A110E] focus:outline-none focus:border-[#1C7C54]/60 disabled:opacity-60 cursor-pointer max-w-[220px]"
      >
        {models.map((m) => (
          <option key={m.id} value={m.id}>{m.label}</option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => setCompareOpen(true)}
        data-testid="ai-compare-btn"
        title="Comparar modelos"
        className="h-8 px-2 rounded-lg border border-[#EBEBEA] hover:border-[#1C7C54]/60 hover:bg-[#1C7C54]/5 text-[#0A110E] flex items-center gap-1 text-xs"
      >
        <GitCompare className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Comparar</span>
      </button>
    </div>
  );

  const inputBar = (
    <form
      onSubmit={(e) => { e.preventDefault(); send(); }}
      className="flex gap-2 pt-3 border-t border-[#EBEBEA]"
    >
      <input
        data-testid="ai-chat-input"
        value={input}
        disabled={sending}
        onChange={(e) => setInput(e.target.value)}
        placeholder="Pregunta sobre tus finanzas…"
        className="flex-1 h-11 px-4 rounded-xl border border-[#EBEBEA] bg-white text-sm focus:outline-none focus:border-[#1C7C54]/60"
      />
      {sending ? (
        <button
          type="button"
          data-testid="ai-chat-stop"
          onClick={stopStreaming}
          className="h-11 px-4 rounded-xl bg-[#D95D39] text-white text-sm font-medium hover:bg-[#B84A2A] transition-colors flex items-center gap-2"
        >
          <Square className="w-3.5 h-3.5 fill-current" />
          Detener
        </button>
      ) : (
        <button
          type="submit"
          data-testid="ai-chat-send"
          disabled={!input.trim()}
          className="h-11 px-4 rounded-xl bg-[#0F291E] text-white text-sm font-medium hover:bg-[#1C7C54] transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          <Send className="w-4 h-4" />
          Enviar
        </button>
      )}
    </form>
  );

  const messages = current?.messages || [];
  const usageTotal = current?.usage_total || EMPTY_USAGE;
  const showUsage = current?.id && (usageTotal.tokens_in > 0 || usageTotal.tokens_out > 0);
  const showBudget = budgetInfo && (budgetInfo.budget > 0 || budgetInfo.spent > 0);

  const budgetIndicator = showBudget && (
    <span
      data-testid="ai-budget-indicator"
      className={`inline-flex items-center gap-1 ${
        budgetInfo.over ? "text-[#D95D39]" : budgetInfo.warn ? "text-[#B8860B]" : "text-[#5F7368]"
      }`}
      title={`Gasto IA acumulado este mes (${budgetInfo.month})`}
    >
      <DollarSign className="w-3 h-3" />
      Este mes ·{" "}
      <span className="font-semibold">
        {formatCost(budgetInfo.spent)}
      </span>
      {budgetInfo.budget > 0 && (
        <span className="text-[#8A9992]"> / {formatCost(budgetInfo.budget)}</span>
      )}
    </span>
  );

  const usageFooter = (showUsage || showBudget) && (
    <div
      data-testid="ai-usage-footer"
      className="flex items-center justify-between gap-3 pt-2 text-[11px] text-[#8A9992] border-t border-dashed border-[#EBEBEA] mt-2 flex-wrap"
    >
      {showUsage ? (
        <span>
          <span className="font-medium text-[#5F7368]">{usageTotal.tokens_in.toLocaleString()}</span> in ·{" "}
          <span className="font-medium text-[#5F7368]">{usageTotal.tokens_out.toLocaleString()}</span> out tokens · esta sesión{" "}
          <span data-testid="ai-usage-cost" className="font-semibold text-[#0A110E]">{formatCost(usageTotal.cost_usd)}</span>
        </span>
      ) : <span />}
      {budgetIndicator}
    </div>
  );

  const emptyState = (
    <div className="flex flex-col items-center justify-center flex-1 gap-4 text-center p-6" data-testid="ai-empty-state">
      <div className="w-14 h-14 rounded-full bg-[#1C7C54]/10 flex items-center justify-center">
        <Sparkles className="w-6 h-6 text-[#1C7C54]" />
      </div>
      <div>
        <h3 className="font-display text-xl font-semibold text-[#0A110E]">Tu asistente financiero</h3>
        <p className="text-sm text-[#5F7368] mt-1 max-w-md">
          Pregúntame lo que quieras. Conozco tus cuentas, inversiones, deudas y movimientos.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-lg mt-2">
        {SUGGESTIONS.map((s, i) => (
          <button
            key={s}
            type="button"
            data-testid={`ai-suggestion-${i}`}
            onClick={() => send(s)}
            className="text-left rounded-xl border border-[#EBEBEA] px-3 py-2 text-sm text-[#0A110E] hover:border-[#1C7C54]/40 hover:bg-[#1C7C54]/5 transition-all"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );

  const chatArea = (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex items-center justify-between pb-3 border-b border-[#EBEBEA] mb-3 gap-3">
        <div className="text-xs uppercase tracking-[0.08em] text-[#8A9992] font-bold truncate">
          {current?.title || "Nuevo chat"}
        </div>
        {modelSelector}
      </div>
      <div
        ref={listRef}
        className="flex-1 overflow-y-auto space-y-4 pr-1"
        data-testid="ai-chat-messages"
      >
        {messages.length === 0 ? emptyState : messages.map((m, i) => (
          <div
            key={`${m.at || i}-${m.role}`}
            data-testid={`ai-msg-${m.role}-${i}`}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${
                m.role === "user"
                  ? "bg-[#0F291E] text-white rounded-br-md"
                  : "bg-[#FAFAF9] border border-[#EBEBEA] text-[#0A110E] rounded-bl-md"
              }`}
            >
              {m.role === "assistant" ? (
                m.content ? (
                  <div className="prose prose-sm max-w-none prose-headings:my-2 prose-p:my-1 prose-ul:my-1 prose-table:my-2 prose-strong:text-[#0A110E]">
                    <ReactMarkdown>{m.content}</ReactMarkdown>
                    {m.streaming && (
                      <span className="inline-block w-1.5 h-3.5 align-middle ml-0.5 bg-[#1C7C54] animate-pulse rounded-sm" />
                    )}
                  </div>
                ) : (
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#8A9992] animate-pulse" />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#8A9992] animate-pulse [animation-delay:150ms]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#8A9992] animate-pulse [animation-delay:300ms]" />
                  </span>
                )
              ) : (
                m.content
              )}
            </div>
          </div>
        ))}
      </div>
      {inputBar}
      {usageFooter}
    </div>
  );

  const compareDialog = (
    <AICompareDialog
      open={compareOpen}
      onOpenChange={setCompareOpen}
      models={models}
      defaultLeft={selectedModel}
      defaultRight={models.find((m) => m.id !== selectedModel)?.id || DEFAULT_MODEL}
      initialPrompt={input}
    />
  );

  if (variant === "widget") {
    return <>{chatArea}{compareDialog}</>;
  }

  // PAGE variant with sidebar
  return (
    <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6 h-[calc(100vh-11rem)]">
      <aside className="card-surface p-4 flex flex-col gap-3 overflow-hidden">
        <button
          onClick={startNew}
          data-testid="ai-new-chat-btn"
          className="flex items-center justify-center gap-2 h-10 rounded-xl bg-[#0F291E] hover:bg-[#1C7C54] text-white text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nueva conversación
        </button>
        <div className="text-xs uppercase tracking-[0.08em] text-[#8A9992] font-bold mt-2">
          Historial
        </div>
        <div className="flex-1 overflow-y-auto -mx-2 px-2 space-y-1">
          {loadingSessions ? (
            <div className="text-xs text-[#8A9992]">Cargando…</div>
          ) : sessions.length === 0 ? (
            <div className="text-xs text-[#8A9992]">Aún no hay chats.</div>
          ) : (
            sessions.map((s) => (
              <div
                key={s.id}
                className={`group flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs cursor-pointer ${
                  current?.id === s.id ? "bg-[#1C7C54]/10 text-[#1C7C54]" : "hover:bg-[#EBEBEA]/60 text-[#0A110E]"
                }`}
                onClick={() => openSession(s.id)}
                data-testid={`ai-session-${s.id}`}
              >
                <MessageSquare className="w-3 h-3 shrink-0 opacity-60" />
                <span className="truncate flex-1">{s.title || "Chat"}</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                  className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-[#D95D39]/10 text-[#D95D39]"
                  data-testid={`ai-delete-${s.id}`}
                  aria-label="Eliminar chat"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))
          )}
        </div>
      </aside>
      <section className="card-surface p-6 flex flex-col min-h-0">
        {chatArea}
      </section>
      {compareDialog}
    </div>
  );
}
